import { AnyAction, Reducer } from 'redux';
import { deepEquals } from 'app/infrastructure/js';
import { withOptionsReducer } from 'app/components/options-handler';
import { STATE } from 'app/constants';
import { appendAction } from './utils/append-action';
import { applySelection } from './utils/apply-selection';
import { compareModel } from './utils/compare-model';
import { activeItemSelected, applySelectionOnActiveItem } from './utils/selected-value';
import {
  ACTION,
  ClearAll,
  RequestBreadcrumbs, ReceiveBreadcrumbs, FailBreadcrumbs,
  RequestMetadata, ReceiveMetadata, FailMetadata,
  RequestAccessCodetables, ReceiveAccessCodetables, FailAccessCodetables,
  ClearList, RequestListBranch, ReceiveListBranch, FailListBranch,
  ExpandListBranchItem, AddListAction, ClearActionErrors,
  UpdateCodetable, UpdateCategory, UpdateActiveValue, UpdateIncludeInactive,
  UpdateVisibility, SelectRow,
  RequestSaveChanges, ReceiveSaveChanges, FailSaveChanges, CancelChanges,
  ClearCodetableValue
} from './action-types';
import { LinkCodesState, Visibility } from './types-store';
import { appendRowSelection } from 'app/area/link-codes/utils/append-row-selection';

export const INITIAL_STATE: LinkCodesState = {
  fetchingBreadcrumbs: false,
  breadcrumbsOptions: [],
  nodeCodetable: undefined,
  fetchingMetadata: false,
  codetableMetadata: undefined,
  fetchingCodetableAccess: undefined,
  codetableAccess: undefined,
  requestedModel: undefined,
  listBranch: [],
  listBranchState: [],
  listActions: [],
  actionErrors: [],
  activeValueError: undefined,
  updating: false,
  codetable: undefined,
  category: undefined,
  includeInactive: true,
  activeValue: undefined,
  activeType: undefined,
  visiblity: Visibility.all,
  lastSelectedRow: undefined,
  selectedRows: []
};

const reducers: { [indexer: string]: Reducer<LinkCodesState> } = {

  [ACTION.CLEAR_ALL]:
    (state: LinkCodesState, action: ClearAll): LinkCodesState => {

      return {
        ...state,
        ...INITIAL_STATE
      };
    },

  [ACTION.CLEAR_CODETABLE_VALUE]:
    (state: LinkCodesState, action: ClearCodetableValue): LinkCodesState => {

      return {
        ...state,
        codetable: undefined
      };
    },

  [ACTION.REQUEST_BREADCRUMBS]:
    (state: LinkCodesState, action: RequestBreadcrumbs): LinkCodesState => {

      return {
        ...state,
        fetchingBreadcrumbs: true
      };
    },

  [ACTION.RECEIVE_BREADCRUMBS]:
    (state: LinkCodesState, action: ReceiveBreadcrumbs): LinkCodesState => {

      return {
        ...state,
        fetchingBreadcrumbs: false,
        breadcrumbsOptions: action.options,
        nodeCodetable: action.nodeCodetable,
        activeType: action.activeType
      };
    },

  [ACTION.FAIL_BREADCRUMBS]:
    (state: LinkCodesState, action: FailBreadcrumbs): LinkCodesState => {

      return {
        ...state,
        fetchingBreadcrumbs: false,
        breadcrumbsOptions: []
      };
    },

  [ACTION.REQUEST_METADATA]:
    (state: LinkCodesState, action: RequestMetadata): LinkCodesState => {

      if (state.codetable?.value !== action.id) {
        return state;
      }

      return {
        ...state,
        fetchingMetadata: true
      };
    },

  [ACTION.RECEIVE_METADATA]:
    (state: LinkCodesState, action: ReceiveMetadata): LinkCodesState => {

      if (state.codetable?.value !== action.id) {
        return state;
      }

      return {
        ...state,
        fetchingMetadata: false,
        codetableMetadata: action.metadata
      };
    },

  [ACTION.FAIL_METADATA]:
    (state: LinkCodesState, action: FailMetadata): LinkCodesState => {

      if (state.codetable?.value !== action.id) {
        return state;
      }

      return {
        ...state,
        fetchingMetadata: false
      };
    },

  [ACTION.REQUEST_ACCESS_CODETABLES]: (state: LinkCodesState, action: RequestAccessCodetables): LinkCodesState => {

    return {
      ...state,
      fetchingCodetableAccess: action.id
    };
  },

  [ACTION.RECEIVE_ACCESS_CODETABLES]: (state: LinkCodesState, action: ReceiveAccessCodetables): LinkCodesState => {

    return action.id === state.fetchingCodetableAccess
      ? {
        ...state,
        fetchingCodetableAccess: undefined,
        codetableAccess: action.accessCodetables
      }
      : state;
  },

  [ACTION.FAIL_ACCESS_CODETABLES]: (state: LinkCodesState, action: FailAccessCodetables): LinkCodesState => {

    return action.id === state.fetchingCodetableAccess
      ? {
        ...state,
        fetchingCodetableAccess: undefined
      }
      : state;
  },

  [ACTION.CLEAR_LIST]:
    (state: LinkCodesState, action: ClearList): LinkCodesState => {

      return {
        ...state,
        requestedModel: action.model,
        listBranch: [],
        listBranchState: [],
        listActions: [],
        actionErrors: [],
        activeValueError: undefined,
        visiblity: Visibility.all,
        lastSelectedRow: undefined,
        selectedRows: []
      };
    },

  [ACTION.REQUEST_LIST_BRANCH]:
    (state: LinkCodesState, action: RequestListBranch): LinkCodesState => {

      if (!compareModel(state.requestedModel, action.model)) {
        return state;
      }

      const currentListBranchState = state.listBranchState
        .find(branch => branch.id === action.id) ||
        { id: action.id, open: true };

      return {
        ...state,
        listBranchState: [
          ...state.listBranchState.filter(branch => branch !== currentListBranchState),
          {
            ...currentListBranchState,
            fetching: true,
            open: action.id === undefined || currentListBranchState.open
          }
        ]
      };
    },

  [ACTION.RECEIVE_LIST_BRANCH]:
    (state: LinkCodesState, action: ReceiveListBranch): LinkCodesState => {

      if (!compareModel(state.requestedModel, action.model)) {
        return state;
      }

      const currentListBranch = state.listBranch
        .find(branch => branch.id === action.id);
      const currentListBranchItems = currentListBranch?.items || [];
      const currentListBranchState = state.listBranchState
        .find(branch => branch.id === action.id) ||
        { id: action.id, open: false };

      return {
        ...state,
        listBranchState: [
          ...state.listBranchState.filter(branch => branch !== currentListBranchState),
          {
            ...currentListBranchState,
            fetching: false
          }
        ],
        listBranch: [
          ...state.listBranch.filter(branch => branch !== currentListBranch),
          {
            id: action.id,
            numberOfResults: action.numberOfResults,
            items: action.clearList
              ? action.listBranchItems
              : [
                ...currentListBranchItems,
                ...action.listBranchItems
              ]
          }
        ]
      };
    },

  [ACTION.FAIL_LIST_BRANCH]:
    (state: LinkCodesState, action: FailListBranch): LinkCodesState => {

      if (!compareModel(state.requestedModel, action.model)) {
        return state;
      }
      const currentListBranch = state.listBranch
        .find(branch => branch.id === action.id) ||
        { id: action.id, numberOfResults: 0, items: [] };
      const currentListBranchState = state.listBranchState
        .find(branch => branch.id === action.id) ||
        { id: action.id, fetching: false, open: false };

      return currentListBranchState.fetching
        ? {
          ...state,
          listBranchState: [
            ...state.listBranchState.filter(branch => branch !== currentListBranchState),
            {
              ...currentListBranchState,
              fetching: false
            }
          ],
          listBranch: [
            ...state.listBranch.filter(branch => branch !== currentListBranch),
            currentListBranch
          ]
        }
        : state;
    },

  [ACTION.EXPAND_LIST_BRANCH_ITEM]:
    (state: LinkCodesState, action: ExpandListBranchItem): LinkCodesState => {

      const currentListBranchState = state.listBranchState.find(branch => branch.id === action.id) ||
        { id: action.id, fetching: false, open: false };

      return {
        ...state,
        listBranchState: [
          ...state.listBranchState.filter(branch => branch !== currentListBranchState),
          {
            ...currentListBranchState,
            open: !currentListBranchState.open
          }
        ],
        ...(currentListBranchState.open && {
          lastSelectedRow: undefined,
          selectedRows: []
        })
      };
    },

  [ACTION.ADD_LIST_ACTION]:
    (state: LinkCodesState, action: AddListAction): LinkCodesState => {

      return {
        ...state,
        listActions: action.actions.reduce(appendAction,
          (state.listActions || [])
        )
      };
    },

  [ACTION.CLEAR_ACTION_ERRORS]:
    (state: LinkCodesState, action: ClearActionErrors): LinkCodesState => {

      return {
        ...state,
        listActions: action.fixListActions
          ? state.listActions?.filter(listAction => !state.actionErrors?.some(
            message => message.parameters?.codeId === listAction.path[listAction.path.length - 1]
          ))
          : state.listActions,
        actionErrors: []
      };
    },

  [ACTION.UPDATE_CODETABLE]:
    (state: LinkCodesState, action: UpdateCodetable): LinkCodesState => {

      return {
        ...state,
        codetable: action.value,
        category: undefined,
        codetableMetadata: undefined
      };
    },

  [ACTION.UPDATE_CATEGORY]:
    (state: LinkCodesState, action: UpdateCategory): LinkCodesState => {

      return {
        ...state,
        category: action.value
      };
    },

  [ACTION.UPDATE_ACTIVE_VALUE]:
    (state: LinkCodesState, action: UpdateActiveValue): LinkCodesState => {

      return {
        ...state,
        activeValue: action.value,
        listActions: [],
        actionErrors: [],
        activeValueError: undefined,
        visiblity: Visibility.all,
        lastSelectedRow: undefined,
        selectedRows: []
      };
    },

  [ACTION.UPDATE_INCLUDE_INACTIVE]:
    (state: LinkCodesState, action: UpdateIncludeInactive): LinkCodesState => {

      return {
        ...state,
        includeInactive: action.value
      };
    },

  [ACTION.UPDATE_VISIBILITY]:
    (state: LinkCodesState, action: UpdateVisibility): LinkCodesState => {

      return {
        ...state,
        visiblity: action.visibility,
        lastSelectedRow: undefined,
        selectedRows: []
      };
    },

  [ACTION.SELECT_ROW]:
    (state: LinkCodesState, action: SelectRow): LinkCodesState => {

      return {
        ...state,
        lastSelectedRow: action.selection.range
          ? state.lastSelectedRow
          : !state.selectedRows.some(path => deepEquals(path, action.selection.path))
            ? action.selection.path[action.selection.path.length - 1]
            : undefined,
        selectedRows: appendRowSelection(
          state.selectedRows,
          state.lastSelectedRow,
          state.listBranch,
          state.listBranchState,
          action.selection
        )
      };
    },

  [ACTION.REQUEST_SAVE_CHANGES]:
    (state: LinkCodesState, action: RequestSaveChanges): LinkCodesState => {

      if (!compareModel(state.requestedModel, action.model) ||
        state.activeValue?.value !== action.activeValue?.value ||
        state.activeType !== action.activeType) {
        return state;
      }

      return {
        ...state,
        updating: true,
        actionErrors: [],
        activeValueError: undefined
      };
    },

  [ACTION.RECEIVE_SAVE_CHANGES]:
    (state: LinkCodesState, action: ReceiveSaveChanges): LinkCodesState => {

      if (!compareModel(state.requestedModel, action.model) ||
        state.activeValue?.value !== action.activeValue?.value ||
        state.activeType !== action.activeType) {
        return state;
      }

      const listBranch = applySelection(
        state.listBranch,
        state.listActions,
        activeItemSelected(action.activeValue, action.activeType),
        applySelectionOnActiveItem(action.activeValue, action.activeType)
      );

      return {
        ...state,
        listActions: [],
        updating: false,
        listBranch
      };
    },

  [ACTION.FAIL_SAVE_CHANGES]:
    (state: LinkCodesState, action: FailSaveChanges): LinkCodesState => {

      if (!compareModel(state.requestedModel, action.model) ||
        state.activeValue?.value !== action.activeValue?.value ||
        state.activeType !== action.activeType) {
        return state;
      }

      return {
        ...state,
        updating: false,
        actionErrors: action.actionErrors,
        activeValueError: action.activeValueError
      };
    },

  [ACTION.CANCEL_CHANGES]:
    (state: LinkCodesState, action: CancelChanges): LinkCodesState => {

      return {
        ...state,
        listActions: [],
        actionErrors: [],
        activeValueError: undefined
      };
    }
};

export const reducer: Reducer<LinkCodesState> = withOptionsReducer(STATE.linkCodes,
  (state: LinkCodesState = INITIAL_STATE, action: AnyAction) => {
    const r = reducers[action.type];
    return (r && r(state, action)) || state;
  });
